// The level builder in the game view. You fly (noclip; the mouse wheel zooms along your view); a
// see-through, textured preview shows what the next click places, on a grid at its height, with
// guides to nearby pieces it lines up with and a jump check to the nearest one. A side panel shows
// the tool, live coordinates and the controls (rebindable: editorKeys.ts). Changes rebuild the
// level at once.

import * as THREE from 'three';
import { assetManifest, manifestNow, sceneryModel, KENNEY_BASE } from '../assets/kenney';
import { PREFABS, prefab } from '../maps/boxlevel/builtin';
import { SHAPES } from '../maps/boxlevel/shapes';
import {
  MATERIALS,
  type LevelBox,
  type LevelDef,
  type MaterialName,
  type Vec3Tuple,
} from '../maps/boxlevel/types';
import { propObject } from '../render/boxLevelView';
import { makeSourceSpace } from '../render/coords';
import { levelMaterial, materialThumbnail } from '../render/proceduralTextures';
import { shapeGeometry } from '../render/shapeGeometry';
import { EditorModel, KIND_LABEL, MOVE_PRESETS, PALETTE, PLACEABLE, THEMES, type Hit } from './editorModel';
import { EditorPanel, type EditorTool } from './editorPanel';
import { Thumbnails } from './thumbnails';
import { airTime } from '../maps/boxlevel/builtin';
import { inputName } from '../input/keybinds';
import {
  EDITOR_ACTIONS,
  defaultEditorBinds,
  editorLookup,
  type EditorAction,
  type EditorBinds,
} from './editorKeys';

export interface EditorHooks {
  /** The level changed: rebuild the playable level and its view (keep the camera where it is). */
  rebuild(def: LevelDef): void;
  toast(msg: string): void;
  /** Play-test the level now. */
  playTest(def: LevelDef): void;
  /** The catalog opened (true) or closed (false): release or take back the mouse. */
  catalog(open: boolean): void;
  /** Open the "open a map to build on" window. */
  openMaps(): void;
  /** Music state and toggle (the panel's music button). */
  music(): { on: boolean; title: string | null };
  toggleMusic(): void;
  /** Screen position (CSS px) of a Source-space point, or null when it's behind the camera. */
  project(p: readonly number[]): [number, number] | null;
  anisotropy: number;
}

/** Arrow keys and PgUp/PgDn with a selection: forward/back and left/right of the view, up/down. */
const NUDGE: Record<string, { fwd: number; side: number; dz: number }> = {
  ArrowUp: { fwd: 1, side: 0, dz: 0 },
  ArrowDown: { fwd: -1, side: 0, dz: 0 },
  ArrowLeft: { fwd: 0, side: -1, dz: 0 },
  ArrowRight: { fwd: 0, side: 1, dz: 0 },
  PageUp: { fwd: 0, side: 0, dz: 1 },
  PageDown: { fwd: 0, side: 0, dz: -1 },
};

const $ = (id: string) => document.getElementById(id)!;
const OUTLINE = 0xffe14a;

/** Placement grid: cells across. */
const GRID_CELLS = 32;
/** Alignment guides: how many, how far to look, colours for same top / same bottom / edge in line. */
const GUIDE_MAX = 8;
const GUIDE_RANGE = 1500;
const GUIDE_COLOURS: [number, number, number][] = [
  [0.35, 1, 0.5],
  [0.35, 0.65, 1],
  [0.85, 0.45, 1],
];
/** Jump check: CS:S jump speed and the highest step up a jump reaches. */
const JUMP_SPEED = 301.993377;
const MAX_JUMP_UP = (JUMP_SPEED * JUMP_SPEED) / (2 * 800);

/** Snap a yaw to the nearest quarter turn (walls, ramps and boosters line up with the axes). */
const snapYaw = (yaw: number) => Math.round(yaw / (Math.PI / 2)) * (Math.PI / 2);

export class LevelEditor {
  model: EditorModel | null = null;
  /** Source-space group for previews. */
  private readonly root = new THREE.Group();
  private readonly preview = new THREE.Group();
  private readonly aimBox: THREE.LineSegments;
  private readonly selectionGroup = new THREE.Group();
  private readonly hud = $('editorHud');
  private builtVersion = -1;
  private previewKey = '';
  private aimHit: Hit | null = null;
  private placeAt: { min: Vec3Tuple; max: Vec3Tuple } | null = null;
  private aimPoint: Vec3Tuple = [0, 0, 0];
  private yaw = 0;
  private copying = false;
  private selectionVersion = '';
  private repeat: { code: string; timer: number } | null = null;
  catalogOpen = false;
  private binds: EditorBinds = defaultEditorBinds();
  private keys = editorLookup(this.binds);
  /** The placement grid (Source space, XY plane) and its cell size. */
  private grid: THREE.LineSegments | null = null;
  private gridSize = 0;
  private gridOn = true;
  private panelOn = true;
  /** Guides to pieces the preview lines up with. */
  private readonly guides: THREE.LineSegments;
  private readonly guideBoxes = new THREE.Group();
  private readonly guidePos = new Float32Array(GUIDE_MAX * 6);
  private readonly guideCol = new Float32Array(GUIDE_MAX * 6);
  private liveText = '';
  private liveAt = 0;
  /** Select pieces or place new ones. */
  tool: EditorTool = 'place';
  /** Height pieces go at when placed in open air. */
  workZ = 0;
  /** Dragging the selection with the Select tool: its bottom plane, the start point, the move so far. */
  private drag: { z: number; from: Vec3Tuple | null; delta: Vec3Tuple } | null = null;
  private readonly dragGroup = new THREE.Group();
  private readonly unitEdges = new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));
  /** Height aid: a line down from the piece and its footprint on what's below. */
  private readonly polePos = new Float32Array(10 * 3);
  private readonly pole: THREE.LineSegments;
  private readonly tag = $('editorTag');
  private readonly panel: EditorPanel;

  constructor(
    scene: THREE.Scene,
    private readonly hooks: EditorHooks,
  ) {
    makeSourceSpace(this.root);
    scene.add(this.root);
    const unit = new THREE.BoxGeometry(1, 1, 1);
    this.aimBox = new THREE.LineSegments(
      new THREE.EdgesGeometry(unit),
      new THREE.LineBasicMaterial({ color: 0xff5050 }),
    );
    const gg = new THREE.BufferGeometry();
    gg.setAttribute('position', new THREE.BufferAttribute(this.guidePos, 3));
    gg.setAttribute('color', new THREE.BufferAttribute(this.guideCol, 3));
    this.guides = new THREE.LineSegments(
      gg,
      new THREE.LineBasicMaterial({ vertexColors: true, depthTest: false, transparent: true }),
    );
    this.guides.frustumCulled = false;
    this.guides.renderOrder = 11;
    const pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.BufferAttribute(this.polePos, 3));
    this.pole = new THREE.LineSegments(
      pg,
      new THREE.LineBasicMaterial({ color: 0x6bb8ff, depthTest: false, transparent: true, opacity: 0.9 }),
    );
    this.pole.frustumCulled = false;
    this.pole.renderOrder = 12;
    this.dragGroup.renderOrder = 12;
    (this.unitEdges.userData as { shared?: boolean }).shared = true;
    this.root.add(
      this.preview,
      this.aimBox,
      this.selectionGroup,
      this.guides,
      this.guideBoxes,
      this.pole,
      this.dragGroup,
    );
    this.root.visible = false;
    for (const o of [this.preview, this.aimBox]) o.renderOrder = 10;
    this.buildCatalog();
    this.panel = new EditorPanel(this.hud, {
      model: () => this.model!,
      tool: () => this.tool,
      setTool: (t) => this.setTool(t),
      binds: () => this.binds,
      workZ: () => this.workZ,
      setWorkZ: (z) => this.setWorkZ(z),
      thumb: (mat, c) => this.thumb(mat, c),
      openCatalog: (tab) => this.openCatalog(tab),
      openMaps: () => this.hooks.openMaps(),
      playTest: () => this.model && this.hooks.playTest(this.model.def),
      sideView: () => this.sideView,
      music: () => this.hooks.music(),
      toggleMusic: () => {
        this.hooks.toggleMusic();
        this.renderHud(true);
      },
      setSideView: (on) => (this.sideView = on),
      refresh: () => this.renderHud(),
      toast: (msg) => this.hooks.toast(msg),
    });
  }

  get active() {
    return this.model !== null;
  }

  start(def: LevelDef) {
    this.model = new EditorModel(def);
    this.builtVersion = this.model.version;
    this.previewKey = '';
    this.drag = null;
    // Open-air pieces start at the height of the start platform's top.
    this.workZ = def.boxes.find((b) => b.kind === 'start')?.max[2] ?? 0;
    this.root.visible = true;
    this.hud.hidden = false;
    void assetManifest().then(() => this.fillCatalogAssets());
    this.renderHud();
  }

  stop() {
    this.closeMenu();
    this.showControls(false);
    this.tag.hidden = true;
    this.drag = null;
    this.model = null;
    this.root.visible = false;
    this.hud.hidden = true;
    this.closeCatalog(false);
    this.stopRepeat();
  }

  /* ---------------- Input ---------------- */

  /** The builder's controls (from Settings). */
  setBinds(binds: EditorBinds) {
    this.binds = binds;
    this.keys = editorLookup(binds);
    this.renderHud(true);
  }

  /** Select or place (the toolbar, Q, 1-7). */
  setTool(t: EditorTool) {
    this.tool = t;
    this.drag = null;
    this.renderHud();
  }

  /** Keys while building. Returns true if the key was used. */
  handleKey(e: KeyboardEvent): boolean {
    const m = this.model;
    if (!m) return false;
    const sel = m.selectedCount > 0;
    // Editor shortcuts with Ctrl (not rebindable, like in other editors).
    if (e.ctrlKey || e.metaKey) {
      const k = e.code;
      if (k === 'KeyZ') this.act(e.shiftKey ? 'redo' : 'undo', false);
      else if (k === 'KeyY') this.act('redo', false);
      else if (k === 'KeyD') this.duplicate();
      else if (k === 'KeyC') this.copySelection();
      else if (k === 'KeyV') this.paste();
      else if (k === 'KeyA') {
        m.selection.boxes.clear();
        m.def.boxes.forEach((_, i) => m.selection.boxes.add(i));
        this.setTool('select');
      } else return false;
      this.renderHud();
      return true;
    }
    if (e.code === 'Escape' && (sel || this.drag)) {
      this.drag = null;
      m.selectOnly(null);
      this.renderHud();
      return true;
    }
    // With a selection, the arrows and PgUp/PgDn move it (Shift: resize it), one grid step.
    if (sel && NUDGE[e.code]) {
      this.nudge(e.code, e.shiftKey);
      this.startRepeat(e.code, () => this.nudge(e.code, e.shiftKey));
      return true;
    }
    const action = this.keys.get(e.code);
    if (!action) return false;
    const info = EDITOR_ACTIONS.find((x) => x.id === action);
    if (e.repeat) return true;
    if (info?.repeat) {
      this.resize(action, e.shiftKey);
      this.startRepeat(e.code, () => this.resize(action, e.shiftKey));
      return true;
    }
    this.act(action, e.shiftKey);
    return true;
  }

  handleKeyUp(e: KeyboardEvent) {
    if (!this.model) return;
    if (this.repeat?.code === e.code) this.stopRepeat();
    if (this.keys.get(e.code) === 'copy') this.endCopy();
  }

  /**
   * A mouse button in the 3D view. Left: place (Place tool) or select and start dragging (Select
   * tool). Returns true if the builder used it.
   */
  handleMouse(
    button: number,
    mods: { ctrl: boolean; shift: boolean } = { ctrl: false, shift: false },
  ): boolean {
    const m = this.model;
    if (!m) return false;
    const action = this.keys.get('Mouse' + button);
    if (action === 'place' && this.tool === 'select') {
      const hit = this.aimHit;
      if (!hit) {
        if (!mods.ctrl && !mods.shift) m.selectOnly(null);
      } else if (mods.ctrl || mods.shift) m.toggleSelect(hit);
      else {
        if (!m.isSelected(hit)) m.selectOnly(hit);
        // Drag on the plane of the selection's bottom.
        const b = m.selectionBounds();
        if (b) this.drag = { z: b.min[2], from: null, delta: [0, 0, 0] };
      }
      this.renderHud();
      return true;
    }
    if (!action) return false;
    this.act(action, false);
    return true;
  }

  handleMouseUp(button: number) {
    const m = this.model;
    if (!m) return;
    if (button === 0 && this.drag) {
      const d = this.drag.delta;
      this.drag = null;
      this.dragGroup.visible = false;
      if (d[0] || d[1] || d[2]) m.moveSelection(d);
      this.renderHud();
    }
    if (this.keys.get('Mouse' + button) === 'copy') this.endCopy();
  }

  /** Right click in the 3D view: a small menu for the piece under the cursor (or paste / deselect). */
  contextMenu(x: number, y: number) {
    const m = this.model;
    if (!m) return;
    const menu = $('editorMenu');
    menu.textContent = '';
    const hit = this.aimHit;
    const item = (label: string, fn: () => void, cls = '') => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      if (cls) b.className = cls;
      b.addEventListener('mousedown', (e) => e.stopPropagation());
      b.addEventListener('click', () => {
        this.closeMenu();
        fn();
        this.renderHud();
      });
      menu.append(b);
    };
    if (hit) {
      const b = hit.type === 'box' ? m.def.boxes[hit.index] : null;
      const name = b ? KIND_LABEL[b.kind].replace(' (no collision)', '') : 'Scenery';
      const head = document.createElement('div');
      head.className = 'head';
      head.textContent = b ? `${name} · Z ${Math.round(b.min[2])} to ${Math.round(b.max[2])}` : name;
      menu.append(head);
      item('Select', () => {
        if (!m.isSelected(hit)) m.selectOnly(hit);
        this.tool = 'select';
      });
      item('Copy its look (place more like it)', () => {
        m.eyedrop(hit);
        m.selectOnly(null);
        this.tool = 'place';
      });
      item('Duplicate', () => {
        if (!m.isSelected(hit)) m.selectOnly(hit);
        this.duplicate();
      });
      if (b) item(`Work height to its top (Z ${Math.round(b.max[2])})`, () => this.setWorkZ(b.max[2]));
      item(
        'Delete',
        () => {
          const err = m.isSelected(hit) ? m.deleteSelection() : m.remove(hit);
          if (err) this.hooks.toast(err);
        },
        'danger',
      );
    } else {
      if (m.clipboard) item('Paste here', () => this.paste());
      if (m.selectedCount) item('Deselect', () => m.selectOnly(null));
      item(`Work height to Z ${Math.round(this.aimPoint[2])}`, () => this.setWorkZ(this.aimPoint[2]));
    }
    menu.hidden = false;
    // Keep it on screen.
    const r = menu.getBoundingClientRect();
    menu.style.left = Math.min(x, window.innerWidth - r.width - 8) + 'px';
    menu.style.top = Math.min(y, window.innerHeight - r.height - 8) + 'px';
  }

  /** Redraw the side panel (e.g. the music button changed). */
  refreshPanel() {
    this.renderHud(true);
  }

  closeMenu() {
    $('editorMenu').hidden = true;
  }

  get menuOpen() {
    return !$('editorMenu').hidden;
  }

  /** Mouse wheel: how far to move the camera along the view (+ forward). Shift moves further. */
  zoomStep(up: boolean, shift: boolean): number {
    const step = Math.max(48, (this.model?.tool.grid ?? 16) * 4) * (shift ? 4 : 1);
    return up ? step : -step;
  }

  private act(action: EditorAction, shift: boolean) {
    const m = this.model!;
    let err: string | null = null;
    switch (action) {
      case 'selectTool':
        this.setTool('select');
        return;
      case 'place': {
        const t = m.tool;
        if (t.mode === 'piece' && this.placeAt) {
          err = m.place(this.placeAt.min, this.placeAt.max, this.yaw);
          // The new piece is selected: nudge it, change it in the panel, or Delete it.
          if (!err && m.lastPlaced >= 0) m.selection.boxes.add(m.lastPlaced);
        } else if (t.mode === 'prop') err = m.placeProp(this.aimPoint, this.yaw);
        else if (t.mode === 'prefab') err = m.placePrefab(this.aimPoint, snapYaw(this.yaw));
        else if (t.mode === 'paste') err = m.paste(this.aimPoint);
        break;
      }
      case 'remove':
        if (m.selectedCount) err = m.deleteSelection();
        else if (this.aimHit) err = m.remove(this.aimHit);
        break;
      case 'pickLook':
        if (this.aimHit) {
          m.eyedrop(this.aimHit);
          this.tool = 'place';
        }
        break;
      case 'kind1':
      case 'kind2':
      case 'kind3':
      case 'kind4':
      case 'kind5':
      case 'kind6':
      case 'kind7':
        m.setKind(PLACEABLE[Number(action.slice(4)) - 1]);
        m.selectOnly(null);
        this.tool = 'place';
        break;
      case 'catalog':
        this.openCatalog();
        break;
      case 'shape':
        if (m.selectedCount) this.cycleSelected('shape', shift ? -1 : 1);
        else m.cycleShape(shift ? -1 : 1);
        break;
      case 'rotate':
        if (m.selectedCount) this.rotateSelected();
        else m.rotate();
        break;
      case 'texture':
        if (m.selectedCount) this.cycleSelected('material', shift ? -1 : 1);
        else this.cycleMaterial(shift ? -1 : 1);
        break;
      case 'colour':
        if (m.selectedCount) this.cycleSelected('color', shift ? -1 : 1);
        else m.cycleColor(shift ? -1 : 1);
        break;
      case 'motion':
        m.cycleMove();
        break;
      case 'sky':
        m.setTheme(m.themeIndex() + (shift ? -1 : 1));
        break;
      case 'grid':
        m.cycleGrid();
        break;
      case 'copy':
        if (m.selectedCount) this.copySelection();
        else this.copying = true;
        break;
      case 'paste':
        this.paste();
        break;
      case 'undo':
        if (!m.undo()) this.hooks.toast('Nothing to undo');
        break;
      case 'redo':
        if (!m.redo()) this.hooks.toast('Nothing to redo');
        break;
      case 'showGrid':
        this.gridOn = !this.gridOn;
        this.hooks.toast(this.gridOn ? 'Grid shown' : 'Grid hidden');
        break;
      case 'showPanel':
        this.panelOn = !this.panelOn;
        break;
      case 'playTest':
        this.hooks.playTest(m.def);
        return;
      default:
        this.resize(action, shift);
        return;
    }
    if (err) this.hooks.toast(err);
    this.renderHud();
  }

  private endCopy() {
    if (!this.copying || !this.model) return;
    this.copying = false;
    const err = this.model.copy(this.aimHit);
    if (err) this.hooks.toast(err);
    else this.copied();
    this.renderHud();
  }

  /** Copy the selection (Ctrl+C) and switch to pasting it. */
  private copySelection() {
    const m = this.model!;
    if (!m.selectedCount) return this.hooks.toast('Select something first, then copy it.');
    const err = m.copy(null);
    if (err) this.hooks.toast(err);
    else {
      this.tool = 'place';
      this.copied();
    }
  }

  private copied() {
    const c = this.model!.clipboard;
    const n = (c?.boxes.length ?? 0) + (c?.props.length ?? 0);
    this.hooks.toast(`Copied ${n} piece${n === 1 ? '' : 's'}: click to paste, 1-7 back to building`);
  }

  private duplicate() {
    const m = this.model!;
    const err = m.duplicateSelection();
    this.hooks.toast(err ?? 'Duplicated: move the copy with the arrow keys or drag it');
    this.tool = 'select';
  }

  /** Arrow keys / PgUp / PgDn with a selection: move it a grid step (Shift: resize it). */
  private nudge(code: string, shift: boolean) {
    const m = this.model!;
    const g = m.tool.grid;
    const n = NUDGE[code];
    // Left/right/up/down follow the camera, snapped to the nearest world axis.
    let axis: 0 | 1 | 2 = 2;
    let sign = n.dz;
    if (!n.dz) {
      const fwd = Math.abs(Math.cos(this.yaw)) >= Math.abs(Math.sin(this.yaw)) ? 0 : 1;
      const fs = fwd === 0 ? Math.sign(Math.cos(this.yaw)) : Math.sign(Math.sin(this.yaw));
      if (n.fwd) {
        axis = fwd;
        sign = n.fwd * fs;
      } else {
        // Right of the view: -Y when facing +X (Source Y points left).
        axis = fwd === 0 ? 1 : 0;
        sign = fwd === 0 ? -n.side * fs : n.side * fs;
      }
    }
    if (shift) {
      const err = m.editSelected((b) => {
        b.max[axis] = Math.max(b.min[axis] + g, b.max[axis] + sign * g);
      });
      if (err) this.hooks.toast(err);
    } else {
      const d: Vec3Tuple = [0, 0, 0];
      d[axis] = sign * g;
      m.moveSelection(d);
    }
    this.renderHud();
  }

  /** Change a look field of the selected pieces to the next/previous option. */
  private cycleSelected(field: 'shape' | 'material' | 'color', step: number) {
    const m = this.model!;
    const first = m.def.boxes[[...m.selection.boxes][0]];
    if (!first) return;
    if (field === 'shape') {
      const order = SHAPES.map((x) => x.id);
      const next = order[(order.indexOf(first.shape ?? 'box') + step + order.length) % order.length];
      m.editSelected((b) => {
        if (b.kind === 'start' || b.kind === 'end' || b.kind === 'checkpoint') return;
        if (next === 'box') delete b.shape;
        else b.shape = next;
      });
    } else if (field === 'material') {
      const all: MaterialName[] = [
        ...MATERIALS,
        ...(manifestNow()?.textures.map((t) => `img:${t.id}` as const) ?? []),
      ];
      const next = all[(all.indexOf(first.material) + step + all.length) % all.length];
      m.editSelected((b) => (b.material = next));
    } else {
      const next =
        PALETTE[(Math.max(0, PALETTE.indexOf(first.color)) + step + PALETTE.length) % PALETTE.length];
      m.editSelected((b) => (b.color = next));
    }
  }

  private rotateSelected() {
    this.model!.editSelected((b) => {
      if (b.kind === 'start' || b.kind === 'end' || b.kind === 'checkpoint') return;
      b.turn = ((b.turn ?? 0) + 1) % 4;
      const cx = (b.min[0] + b.max[0]) / 2;
      const cy = (b.min[1] + b.max[1]) / 2;
      const hx = (b.max[1] - b.min[1]) / 2;
      const hy = (b.max[0] - b.min[0]) / 2;
      b.min[0] = cx - hx;
      b.max[0] = cx + hx;
      b.min[1] = cy - hy;
      b.max[1] = cy + hy;
      if (!b.turn) delete b.turn;
    });
  }

  /** Size keys for the next piece; PgUp/PgDn without a selection move the work height. */
  private resize(action: EditorAction, shift: boolean) {
    const m = this.model!;
    const g = m.tool.grid;
    if (action === 'taller' && !shift) this.setWorkZ(this.workZ + g);
    else if (action === 'shorter' && !shift) this.setWorkZ(this.workZ - g);
    else if (action === 'taller') m.resize(2, 1);
    else if (action === 'shorter') m.resize(2, -1);
    else if (action === 'wider') m.resize(0, 1);
    else if (action === 'narrower') m.resize(0, -1);
    else if (action === 'deeper') m.resize(shift ? 2 : 1, 1);
    else if (action === 'shallower') m.resize(shift ? 2 : 1, -1);
    else return;
    this.renderHud();
  }

  /** Height new pieces go at in open air (snapped to the grid). */
  setWorkZ(z: number) {
    const g = this.model?.tool.grid ?? 16;
    this.workZ = Math.max(-16384, Math.min(16384, Math.round(z / g) * g));
    this.hooks.toast(`Work height Z ${this.workZ}`);
    this.renderHud();
  }

  /** Held keys keep repeating: a short pause, then steadily. */
  private startRepeat(code: string, fn: () => void) {
    this.stopRepeat();
    const tick = () => {
      if (!this.repeat) return;
      fn();
      this.repeat.timer = window.setTimeout(tick, 70);
    };
    this.repeat = { code, timer: window.setTimeout(tick, 280) };
  }

  private stopRepeat() {
    if (this.repeat) clearTimeout(this.repeat.timer);
    this.repeat = null;
  }

  private paste() {
    const m = this.model!;
    if (!m.clipboard) return this.hooks.toast('Nothing copied yet: select something and press Ctrl+C');
    const err = m.paste(this.aimPoint);
    if (err) this.hooks.toast(err);
  }

  private cycleMaterial(step: number) {
    const m = this.model!;
    const all: MaterialName[] = [
      ...MATERIALS,
      ...(manifestNow()?.textures.map((t) => `img:${t.id}` as const) ?? []),
    ];
    const i = all.indexOf(m.tool.material);
    m.setMaterial(all[(i + step + all.length) % all.length]);
  }

  /* ---------------- Every frame ---------------- */

  /**
   * `origin`/`dir`: the ray under the mouse cursor (or the view centre while looking around), in
   * Source space. `yaw`: the view direction (pieces and boosters face it).
   */
  update(origin: readonly number[], dir: readonly number[], yaw: number) {
    const m = this.model;
    if (!m) return;
    if (m.version !== this.builtVersion) {
      this.builtVersion = m.version;
      this.hooks.rebuild(m.def);
      this.renderHud();
    }
    this.yaw = yaw;
    if (!this.hover && !this.drag) {
      // The mouse is over the panel: nothing to place or point at in the 3D view.
      this.aimHit = null;
      this.placeAt = null;
      for (const o of [this.preview, this.aimBox, this.pole, this.guides, this.guideBoxes]) o.visible = false;
      if (this.grid) this.grid.visible = false;
      this.tag.hidden = true;
      this.updateSelection();
      return;
    }
    this.guides.visible = this.guideBoxes.visible = true;
    const hit = m.pick(origin, dir);
    this.aimHit = hit;
    // In open air, aim at the work plane: pieces land at a height you chose, not floating ahead.
    const plane = this.planePoint(origin, dir, this.workZ);
    this.aimPoint = hit ? hit.point : (plane ?? m.aim(origin, dir).point);
    if (this.copying && hit) m.select(hit);
    const t = m.tool;
    const placing = this.tool === 'place' && !this.drag;
    if (placing && t.mode === 'piece') {
      if (hit) this.placeAt = m.ghost(origin, dir);
      else if (plane) {
        const g = t.grid;
        const s = t.size;
        const min: Vec3Tuple = [
          Math.round((plane[0] - s[0] / 2) / g) * g,
          Math.round((plane[1] - s[1] / 2) / g) * g,
          this.workZ,
        ];
        this.placeAt = { min, max: [min[0] + s[0], min[1] + s[1], min[2] + s[2]] };
      } else this.placeAt = m.ghost(origin, dir);
      this.setPreview(this.placeAt.min, this.placeAt.max);
      this.preview.visible = true;
    } else if (placing) {
      this.placeAt = null;
      this.setPreview(null, null);
      this.preview.visible = true;
      this.preview.position.set(this.aimPoint[0], this.aimPoint[1], this.aimPoint[2]);
      if (t.mode === 'prop') this.preview.rotation.z = (t.turn * Math.PI) / 2 + yaw;
      else this.preview.rotation.z = 0;
    } else {
      this.placeAt = null;
      this.preview.visible = false;
    }
    this.updateDrag(origin, dir);
    this.updateGrid();
    const align = this.updateGuides();
    this.updateLive(align);
    // Outline of what's under the cursor: red in Place (Delete removes it), white in Select.
    if (hit && !(this.tool === 'select' && m.isSelected(hit))) {
      const r = this.boundsOf(hit);
      this.place(this.aimBox, r.min, r.max, 3);
      (this.aimBox.material as THREE.LineBasicMaterial).color.setHex(
        this.tool === 'select' ? 0xffffff : 0xff5050,
      );
      this.aimBox.visible = true;
    } else this.aimBox.visible = false;
    this.updateSelection();
    this.updateHeight();
  }

  /** The piece being placed or the selection (side view centre and size), or null. */
  focusBox(): { min: number[]; max: number[] } | null {
    const m = this.model;
    if (!m) return null;
    if (this.placeAt) return this.placeAt;
    const b = m.selectionBounds();
    if (!b) return null;
    const d = this.drag?.delta ?? [0, 0, 0];
    return { min: b.min.map((v, k) => v + d[k]), max: b.max.map((v, k) => v + d[k]) };
  }

  /** The mouse is over the 3D view (not the panel or a menu). */
  private hover = true;

  setHover(on: boolean) {
    this.hover = on;
  }

  /** Side view on (the panel's toggle). */
  sideView = true;

  /** Where the ray meets the horizontal plane at `z` (in front, within reach), or null. */
  private planePoint(o: readonly number[], d: readonly number[], z: number): Vec3Tuple | null {
    if (Math.abs(d[2]) < 1e-4) return null;
    const t = (z - o[2]) / d[2];
    if (t <= 0 || t > 30000) return null;
    return [o[0] + d[0] * t, o[1] + d[1] * t, z];
  }

  /** Dragging the selection: follow the cursor on its bottom plane, snapped to the grid. */
  private updateDrag(o: readonly number[], d: readonly number[]) {
    const drag = this.drag;
    const m = this.model!;
    if (!drag) {
      this.dragGroup.visible = false;
      return;
    }
    const p = this.planePoint(o, d, drag.z);
    if (!p) return;
    if (!drag.from) {
      drag.from = p;
      return;
    }
    const g = m.tool.grid;
    drag.delta = [Math.round((p[0] - drag.from[0]) / g) * g, Math.round((p[1] - drag.from[1]) / g) * g, 0];
    const key = [...m.selection.boxes].join(',') + '|' + m.version;
    if (this.dragGroup.userData.key !== key) {
      this.dragGroup.userData.key = key;
      this.clearGroup(this.dragGroup);
      const mat = new THREE.LineBasicMaterial({ color: OUTLINE, depthTest: false, transparent: true });
      for (const i of m.selection.boxes) {
        const b = m.def.boxes[i];
        const l = new THREE.LineSegments(this.unitEdges, mat);
        this.place(l, b.min, b.max, 2);
        this.dragGroup.add(l);
      }
    }
    this.dragGroup.position.set(drag.delta[0], drag.delta[1], 0);
    this.dragGroup.visible = !!(drag.delta[0] || drag.delta[1]);
  }

  /**
   * The height aid: a line from the piece being placed (or the selection) down to what's under
   * it, its footprint drawn there, and a label with the height.
   */
  private updateHeight() {
    const m = this.model!;
    const sel = m.selectionBounds();
    const moved = this.drag && sel ? this.drag.delta : null;
    const box =
      this.placeAt ??
      (sel
        ? {
            min: [sel.min[0] + (moved?.[0] ?? 0), sel.min[1] + (moved?.[1] ?? 0), sel.min[2]] as Vec3Tuple,
            max: [sel.max[0] + (moved?.[0] ?? 0), sel.max[1] + (moved?.[1] ?? 0), sel.max[2]] as Vec3Tuple,
          }
        : null);
    if (!box) {
      this.pole.visible = false;
      this.tag.hidden = true;
      return;
    }
    // The highest piece top under the footprint, at or below the box's bottom.
    let below: number | null = null;
    for (const [i, b] of m.def.boxes.entries()) {
      if (b.kind === 'deco' || (!this.placeAt && m.selection.boxes.has(i))) continue;
      if (
        b.min[0] >= box.max[0] ||
        b.max[0] <= box.min[0] ||
        b.min[1] >= box.max[1] ||
        b.max[1] <= box.min[1]
      )
        continue;
      if (b.max[2] <= box.min[2] + 0.5 && (below === null || b.max[2] > below)) below = b.max[2];
    }
    const floor = below ?? box.min[2] - 512;
    const cx = (box.min[0] + box.max[0]) / 2;
    const cy = (box.min[1] + box.max[1]) / 2;
    const p = this.polePos;
    // Line straight down from the bottom centre.
    p.set([cx, cy, box.min[2], cx, cy, floor + 0.5], 0);
    // Footprint where it would land.
    const z = floor + 0.5;
    const c = [
      [box.min[0], box.min[1]],
      [box.max[0], box.min[1]],
      [box.max[0], box.max[1]],
      [box.min[0], box.max[1]],
    ];
    for (let i = 0; i < 4; i++) {
      const a = c[i];
      const b = c[(i + 1) % 4];
      p.set([a[0], a[1], z, b[0], b[1], z], 6 + i * 6);
    }
    this.pole.geometry.attributes.position.needsUpdate = true;
    (this.pole.material as THREE.LineBasicMaterial).color.setHex(below === null ? 0xff6b6b : 0x6bb8ff);
    this.pole.visible = true;
    // The label, beside the top of the piece.
    const at = this.hooks.project([box.max[0], cy, box.max[2]]);
    if (!at) {
      this.tag.hidden = true;
      return;
    }
    const gap = below === null ? null : Math.round(box.min[2] - below);
    const text =
      `Z ${Math.round(box.min[2])}` +
      (gap === null
        ? ' · nothing below it'
        : gap === 0
          ? ' · sits on the piece below'
          : ` · ${gap} above the piece below`);
    if (this.tag.textContent !== text) this.tag.textContent = text;
    this.tag.classList.toggle('air', gap === null);
    this.tag.style.transform = `translate(${Math.round(at[0]) + 14}px, ${Math.round(at[1]) - 10}px)`;
    this.tag.hidden = false;
  }

  private boundsOf(hit: Hit): { min: number[]; max: number[] } {
    const m = this.model!;
    if (hit.type === 'box') return m.def.boxes[hit.index];
    const p = m.def.props![hit.index];
    const r = p.scale * 0.6;
    return {
      min: [p.pos[0] - r, p.pos[1] - r, p.pos[2]],
      max: [p.pos[0] + r, p.pos[1] + r, p.pos[2] + r * 2.5],
    };
  }

  private place(o: THREE.Object3D, min: readonly number[], max: readonly number[], grow = 0) {
    o.scale.set(max[0] - min[0] + grow, max[1] - min[1] + grow, max[2] - min[2] + grow);
    o.position.set((min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2);
  }

  /** Rebuild the preview when the tool changes; move it every frame. */
  private setPreview(min: Vec3Tuple | null, max: Vec3Tuple | null) {
    const m = this.model!;
    const t = m.tool;
    const key = JSON.stringify([
      t.mode,
      t.kind,
      t.shape,
      t.turn,
      t.material,
      t.color,
      t.size,
      t.model,
      t.modelScale,
      t.prefab,
      m.version,
      !!m.clipboard,
    ]);
    if (key !== this.previewKey) {
      this.previewKey = key;
      this.clearGroup(this.preview);
      if (t.mode === 'piece') this.previewPiece();
      else if (t.mode === 'prop') void this.previewProp();
      else if (t.mode === 'prefab') this.previewOutlines(m.prefabPieces([0, 0, 0], snapYaw(this.yaw)));
      else if (t.mode === 'paste' && m.clipboard) this.previewOutlines(m.clipboard.boxes);
    }
    if (t.mode === 'prefab') {
      // Prefabs follow your facing; rebuild when it changes quarter.
      const k2 = this.previewKey + snapYaw(this.yaw);
      if (this.preview.userData.yaw !== k2) {
        this.preview.userData.yaw = k2;
        this.clearGroup(this.preview);
        this.previewOutlines(m.prefabPieces([0, 0, 0], snapYaw(this.yaw)));
      }
    }
    if (min && max)
      this.preview.position.set((min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2);
  }

  /** The tool piece, textured and see-through, with a bright double outline. */
  private previewPiece() {
    const t = this.model!.tool;
    const geo = shapeGeometry(t.shape, t.size, [0, 0, 0], t.turn, false);
    const mat = levelMaterial(t.material, t.color, this.hooks.anisotropy).clone();
    mat.transparent = true;
    mat.opacity = 0.6;
    mat.depthWrite = false;
    mat.emissive = new THREE.Color(0x222222);
    this.preview.add(new THREE.Mesh(geo, mat));
    const edges = new THREE.EdgesGeometry(geo, 20);
    this.preview.add(
      new THREE.LineSegments(
        edges,
        new THREE.LineBasicMaterial({ color: OUTLINE, depthTest: false, transparent: true }),
      ),
    );
    const outer = new THREE.LineSegments(
      edges,
      new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5 }),
    );
    outer.scale.setScalar(1.03);
    this.preview.add(outer);
  }

  private async previewProp() {
    const t = this.model!.tool;
    const key = this.previewKey;
    const model = await sceneryModel(t.model);
    if (!model || key !== this.previewKey) return;
    const o = propObject(model, { model: t.model, pos: [0, 0, 0], yaw: 0, scale: t.modelScale });
    o.traverse((x) => {
      const mesh = x as THREE.Mesh;
      if (mesh.material) {
        const mm = (mesh.material as THREE.Material).clone();
        mm.transparent = true;
        mm.opacity = 0.7;
        mesh.material = mm;
      }
    });
    this.preview.add(o);
  }

  private previewOutlines(boxes: LevelBox[]) {
    const mat = new THREE.LineBasicMaterial({ color: OUTLINE, depthTest: false, transparent: true });
    for (const b of boxes) {
      const geo = shapeGeometry(
        b.shape ?? 'box',
        [b.max[0] - b.min[0], b.max[1] - b.min[1], b.max[2] - b.min[2]],
        [0, 0, 0],
        b.turn ?? 0,
      );
      const l = new THREE.LineSegments(new THREE.EdgesGeometry(geo, 20), mat);
      geo.dispose();
      l.position.set((b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2);
      this.preview.add(l);
    }
  }

  private updateSelection() {
    const m = this.model!;
    const key = [...m.selection.boxes].join(',') + '|' + [...m.selection.props].join(',') + '|' + m.version;
    if (key === this.selectionVersion) return;
    this.selectionVersion = key;
    this.clearGroup(this.selectionGroup);
    const mat = new THREE.LineBasicMaterial({ color: 0x4ae1ff, depthTest: false, transparent: true });
    const unit = new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));
    const add = (min: number[], max: number[]) => {
      const l = new THREE.LineSegments(unit, mat);
      this.place(l, min, max, 4);
      this.selectionGroup.add(l);
    };
    for (const i of m.selection.boxes) add(m.def.boxes[i].min, m.def.boxes[i].max);
    for (const i of m.selection.props) {
      const b = this.boundsOf({ type: 'prop', index: i, t: 0, point: [0, 0, 0], normal: [0, 0, 1] });
      add(b.min, b.max);
    }
  }

  private clearGroup(g: THREE.Group) {
    for (const c of [...g.children]) {
      g.remove(c);
      c.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.geometry && !(mesh.geometry.userData as { shared?: boolean }).shared)
          mesh.geometry.dispose();
      });
    }
  }

  /* ---------------- Grid and guides ---------------- */

  /** A grid of the snap size at the height the next piece goes, following it. */
  private updateGrid() {
    const m = this.model!;
    const at = this.placeAt;
    const show = this.gridOn && !!at;
    if (m.tool.grid !== this.gridSize || !this.grid) {
      this.gridSize = m.tool.grid;
      if (this.grid) {
        this.root.remove(this.grid);
        this.grid.geometry.dispose();
      }
      const n = GRID_CELLS;
      const half = (n * this.gridSize) / 2;
      const pts: number[] = [];
      const cols: number[] = [];
      for (let i = 0; i <= n; i++) {
        const v = -half + i * this.gridSize;
        // Every 4th line brighter, the middle ones brightest, all fading toward the edge.
        const major = i % 4 === 0;
        const fade = 1 - Math.abs(i - n / 2) / (n / 2);
        const c = (major ? 0.55 : 0.28) * (0.35 + 0.65 * fade);
        pts.push(v, -half, 0, v, half, 0, -half, v, 0, half, v, 0);
        for (let k = 0; k < 4; k++) cols.push(c, c * 0.95, c * 0.8);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
      g.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
      this.grid = new THREE.LineSegments(
        g,
        new THREE.LineBasicMaterial({
          vertexColors: true,
          transparent: true,
          opacity: 0.85,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      this.grid.frustumCulled = false;
      this.grid.renderOrder = 9;
      this.root.add(this.grid);
    }
    this.grid.visible = show;
    if (!at) return;
    const gs = this.gridSize;
    const cx = Math.round((at.min[0] + at.max[0]) / 2 / gs) * gs;
    const cy = Math.round((at.min[1] + at.max[1]) / 2 / gs) * gs;
    this.grid.position.set(cx, cy, at.min[2] + 0.5);
  }

  /**
   * Nearby pieces the preview lines up with: same top height (green), same bottom (blue), an edge in
   * line (purple). They get an outline and a guide line from the preview.
   */
  private updateGuides(): { top: number; bottom: number; edge: number } {
    const m = this.model!;
    const at = this.placeAt;
    const out = { top: 0, bottom: 0, edge: 0 };
    let n = 0;
    for (const o of this.guideBoxes.children) o.visible = false;
    if (at) {
      const cx = (at.min[0] + at.max[0]) / 2;
      const cy = (at.min[1] + at.max[1]) / 2;
      const found: { b: LevelBox; type: 0 | 1 | 2; d: number }[] = [];
      for (const b of m.def.boxes) {
        if (b.kind === 'deco') continue;
        const bx = (b.min[0] + b.max[0]) / 2;
        const by = (b.min[1] + b.max[1]) / 2;
        const d = Math.hypot(bx - cx, by - cy);
        if (d > GUIDE_RANGE || d < 1) continue;
        const near = (p: number, q: number) => Math.abs(p - q) < 0.5;
        let type: 0 | 1 | 2 | -1 = -1;
        if (near(b.max[2], at.max[2])) type = 0;
        else if (near(b.min[2], at.min[2])) type = 1;
        else if (
          [0, 1].some((k) => [b.min[k], b.max[k]].some((e) => near(e, at.min[k]) || near(e, at.max[k])))
        )
          type = 2;
        if (type < 0) continue;
        out[type === 0 ? 'top' : type === 1 ? 'bottom' : 'edge']++;
        found.push({ b, type: type as 0 | 1 | 2, d });
      }
      found.sort((p, q) => p.type - q.type || p.d - q.d);
      for (const { b, type } of found.slice(0, GUIDE_MAX)) {
        const col = GUIDE_COLOURS[type];
        let box = this.guideBoxes.children[n] as THREE.LineSegments | undefined;
        if (!box) {
          box = new THREE.LineSegments(
            new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)),
            new THREE.LineBasicMaterial({ depthTest: false, transparent: true }),
          );
          box.renderOrder = 11;
          this.guideBoxes.add(box);
        }
        (box.material as THREE.LineBasicMaterial).color.setRGB(col[0], col[1], col[2]);
        this.place(box, b.min, b.max, 2);
        box.visible = true;
        // Guide line between the faces that match.
        const z1 = type === 1 ? at.min[2] : at.max[2];
        const z2 = type === 1 ? b.min[2] : b.max[2];
        this.guidePos.set([cx, cy, z1, (b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, z2], n * 6);
        this.guideCol.set([...col, ...col], n * 6);
        n++;
      }
    }
    const g = this.guides.geometry;
    g.setDrawRange(0, n * 2);
    g.attributes.position.needsUpdate = true;
    g.attributes.color.needsUpdate = true;
    return out;
  }

  /** Nearest other piece from the preview: the gap, the rise, and whether a jump makes it. */
  private jumpCheck(): { verdict: string; cls: 'ok' | 'warn' | 'bad'; detail: string } | null {
    const m = this.model!;
    const at = this.placeAt;
    if (!at) return null;
    let best: LevelBox | null = null;
    let bestGap = Infinity;
    for (const b of m.def.boxes) {
      if (b.kind === 'deco') continue;
      const gx = Math.max(0, b.min[0] - at.max[0], at.min[0] - b.max[0]);
      const gy = Math.max(0, b.min[1] - at.max[1], at.min[1] - b.max[1]);
      const gap = Math.hypot(gx, gy);
      if (gap === 0 && b.max[2] <= at.min[2] + 0.5) continue; // the piece it sits on
      if (gap < bestGap) {
        bestGap = gap;
        best = b;
      }
    }
    if (!best || bestGap > 1200) return null;
    const rise = best.max[2] - at.max[2];
    const gap = Math.round(bestGap);
    const detail = `Nearest piece: ${gap} away, ${rise > 0 ? '+' : ''}${Math.round(rise)} high`;
    if (gap <= 32 && rise <= 18) return { verdict: 'Walkable', cls: 'ok', detail };
    // Jump from the preview to it: time in the air, then the speed that covers the gap.
    const t = airTime(JUMP_SPEED, rise);
    if (t <= 0) return { verdict: `Too high to jump (max +${Math.floor(MAX_JUMP_UP)})`, cls: 'bad', detail };
    const need = Math.max(0, (gap - 32) / t);
    if (need <= 250) return { verdict: 'Easy jump', cls: 'ok', detail };
    if (need <= 400) return { verdict: 'Jumpable while running', cls: 'ok', detail };
    if (need <= 900)
      return { verdict: `Needs bhop speed (~${Math.round(need / 50) * 50} u/s)`, cls: 'warn', detail };
    return { verdict: 'Too far to jump', cls: 'bad', detail };
  }

  /** Where the piece goes, what it lines up with and the jump check (refreshed a few times a second). */
  private updateLive(align: { top: number; bottom: number; edge: number }) {
    const now = performance.now();
    if (now - this.liveAt < 100) return;
    this.liveAt = now;
    const m = this.model!;
    const at = this.placeAt;
    const f = (v: number) => String(Math.round(v));
    const xyz = (v: readonly number[]) =>
      `<span class="xyz"><span class="x">${f(v[0])}</span><span class="y">${f(v[1])}</span><span class="z">${f(v[2])}</span></span>`;
    const lines: string[] = [];
    lines.push(
      `<div class="row"><span class="lbl">${at ? 'Placing at' : 'Aiming at'}</span>${xyz(at ? at.min : this.aimPoint)}</div>`,
    );
    if (at)
      lines.push(`<div class="row"><span class="lbl">Top height</span><span>${f(at.max[2])}</span></div>`);
    const al: string[] = [];
    if (align.top) al.push(`<span class="pill gTop">Same top as ${align.top}</span>`);
    if (align.bottom) al.push(`<span class="pill gBottom">Same bottom as ${align.bottom}</span>`);
    if (align.edge) al.push(`<span class="pill gEdge">Edge in line with ${align.edge}</span>`);
    if (al.length) lines.push(`<div class="pills">${al.join('')}</div>`);
    const jump = m.tool.mode === 'piece' ? this.jumpCheck() : null;
    if (jump)
      lines.push(
        `<div class="jump"><span class="pill ${jump.cls}">${jump.verdict}</span><small>${jump.detail}</small></div>`,
      );
    const html = lines.join('');
    if (html === this.liveText) return;
    this.liveText = html;
    const el = this.panel.liveEl;
    if (el) el.innerHTML = html;
  }

  /* ---------------- Side panel ---------------- */

  private readonly thumbs = new Map<string, string>();

  /** Small picture of a texture in a colour (cached). */
  private thumb(material: MaterialName, color: string): string {
    const key = material + color;
    let url = this.thumbs.get(key);
    if (url === undefined) {
      if (material.startsWith('img:')) {
        const t = manifestNow()?.textures.find((x) => 'img:' + x.id === material);
        url = t ? KENNEY_BASE + t.file : '';
      } else url = materialThumbnail(material as (typeof MATERIALS)[number], color, 64);
      this.thumbs.set(key, url);
    }
    return url;
  }

  private renderHud(force = false) {
    if (!this.model) return;
    this.hud.classList.toggle('collapsed', !this.panelOn);
    this.panel.render(force);
    if (this.controlsOn) this.renderControls();
  }

  /** Catalog picks change the selection when there is one, else the next piece. */
  private pickShape(id: (typeof SHAPES)[number]['id']) {
    const m = this.model!;
    if (m.selectedCount)
      m.editSelected((b) => {
        if (b.kind === 'start' || b.kind === 'end' || b.kind === 'checkpoint') return;
        if (id === 'box') delete b.shape;
        else b.shape = id;
      });
    else m.setShape(id);
  }

  private pickMaterial(name: MaterialName) {
    const m = this.model!;
    if (m.selectedCount) m.editSelected((b) => (b.material = name));
    else m.setMaterial(name);
  }

  private pickColor(c: string) {
    const m = this.model!;
    if (m.selectedCount) m.editSelected((b) => (b.color = c));
    else m.setColor(c);
  }

  /* ---------------- All controls (held key) ---------------- */

  /** Name of the key that shows every control (the game's hiscores key). */
  controlsKey = 'Tab';
  private controlsOn = false;

  /** Show the sheet with every builder control while the key is held. */
  showControls(on: boolean) {
    if (on && !this.model) on = false;
    if (on === this.controlsOn) return;
    this.controlsOn = on;
    $('editorKeys').hidden = !on;
    if (on) this.renderControls();
  }

  private renderControls() {
    const groups = new Map<string, string[]>();
    const add = (group: string, keys: string, label: string) => {
      const list = groups.get(group) ?? [];
      list.push(`<li><span class="k">${keys}</span><span>${label}</span></li>`);
      groups.set(group, list);
    };
    add('Fly', '<kbd>Wheel button</kbd>', 'Hold to look around');
    add('Fly', '<kbd>Move keys</kbd>', 'Fly around');
    add('Fly', '<kbd>Jump</kbd> <kbd>Crouch</kbd>', 'Up / down');
    add('Fly', '<kbd>Wheel</kbd>', 'Zoom along your view (Shift: faster)');
    for (const a of EDITOR_ACTIONS) {
      if (a.id.startsWith('kind')) {
        if (a.id === 'kind1') add('Place', '<kbd>1</kbd>–<kbd>7</kbd>', 'Piece type');
        continue;
      }
      const keys = this.binds[a.id].map((c) => `<kbd>${inputName(c)}</kbd>`).join('') || '<i>not set</i>';
      add(a.group, keys, a.label);
    }
    add('Selection', '<kbd>Right click</kbd>', 'Menu: select, copy look, duplicate, delete, work height');
    add('Selection', '<kbd>Click</kbd>', 'Select (Select tool); Ctrl+click adds more');
    add('Selection', '<kbd>Drag</kbd>', 'Move the selection');
    add(
      'Selection',
      '<kbd>←</kbd><kbd>→</kbd><kbd>↑</kbd><kbd>↓</kbd>',
      'Move it one grid step (Shift: resize)',
    );
    add('Selection', '<kbd>PgUp</kbd><kbd>PgDn</kbd>', 'Up / down (no selection: work height)');
    add('Selection', '<kbd>Ctrl</kbd>+<kbd>D</kbd>', 'Duplicate');
    add('Selection', '<kbd>Ctrl</kbd>+<kbd>C</kbd> / <kbd>V</kbd>', 'Copy / paste');
    add('Selection', '<kbd>Ctrl</kbd>+<kbd>Z</kbd> / <kbd>Y</kbd>', 'Undo / redo');
    add('Selection', '<kbd>Esc</kbd>', 'Deselect (then: menu)');
    $('editorKeys').innerHTML =
      '<header><b>Level builder controls</b><span>Change them under Settings</span></header><div class="cols">' +
      [...groups].map(([g, items]) => `<section><h4>${g}</h4><ul>${items.join('')}</ul></section>`).join('') +
      '</div>';
  }

  /* ---------------- Catalog ---------------- */

  private catalogTab = 'pieces';

  openCatalog(tab?: string) {
    if (!this.model) return;
    this.catalogOpen = true;
    $('catalog').hidden = false;
    this.showTab(tab ?? this.catalogTab);
    this.hooks.catalog(true);
  }

  /** Close; with `relock`, take the mouse back (must run inside a click). */
  closeCatalog(relock: boolean) {
    if (!this.catalogOpen) return;
    this.catalogOpen = false;
    $('catalog').hidden = true;
    this.renderHud();
    if (relock) this.hooks.catalog(false);
  }

  private showTab(id: string) {
    this.catalogTab = id;
    for (const b of document.querySelectorAll<HTMLElement>('#catalogTabs button'))
      b.classList.toggle('on', b.dataset.tab === id);
    for (const p of document.querySelectorAll<HTMLElement>('.catalogPage')) p.hidden = p.dataset.tab !== id;
  }

  /** Pictures for the catalog, drawn when their tiles come into view. */
  private pictures: Thumbnails | null = null;
  private thumbWatch: IntersectionObserver | null = null;
  private readonly thumbLoad = new Map<Element, () => Promise<string | null>>();

  /** Draw a tile's picture the first time it scrolls into view. */
  private lazyPicture(el: HTMLImageElement, picture: (t: Thumbnails) => Promise<string | null>) {
    this.thumbWatch ??= new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const load = this.thumbLoad.get(e.target);
          this.thumbWatch!.unobserve(e.target);
          this.thumbLoad.delete(e.target);
          void load?.().then((url) => {
            if (!url) return;
            const img = e.target as HTMLImageElement;
            img.src = url;
            img.style.background = '';
          });
        }
      },
      { root: $('catalogPages'), rootMargin: '200px' },
    );
    this.thumbLoad.set(el, () => picture((this.pictures ??= new Thumbnails(this.hooks.anisotropy))));
    this.thumbWatch.observe(el);
  }

  private tile(
    page: HTMLElement,
    label: string,
    pick: () => void,
    img?: string,
    swatch?: string,
    picture?: (t: Thumbnails) => Promise<string | null>,
  ) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'tile';
    if (picture) {
      // A placeholder in the tile's colour until the picture is drawn.
      const i = document.createElement('img');
      i.alt = '';
      if (swatch) i.style.background = swatch;
      b.append(i);
      this.lazyPicture(i, picture);
    } else if (img) {
      const i = document.createElement('img');
      i.src = img;
      i.alt = '';
      i.loading = 'lazy';
      b.append(i);
    } else if (swatch) {
      const s = document.createElement('i');
      s.style.background = swatch;
      b.append(s);
    }
    const t = document.createElement('span');
    t.textContent = label;
    b.append(t);
    b.addEventListener('click', () => {
      pick();
      this.closeCatalog(true);
    });
    page.append(b);
  }

  private page(id: string, title: string): HTMLElement {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.textContent = title;
    tab.dataset.tab = id;
    tab.addEventListener('click', () => this.showTab(id));
    $('catalogTabs').append(tab);
    const p = document.createElement('div');
    p.className = 'catalogPage';
    p.dataset.tab = id;
    p.hidden = true;
    $('catalogPages').append(p);
    return p;
  }

  private buildCatalog() {
    $('catalogClose').addEventListener('click', () => this.closeCatalog(true));
    const m = () => this.model!;
    const pieces = this.page('pieces', 'Pieces');
    PLACEABLE.forEach((k) => {
      const [material, color] = KIND_LOOK[k];
      this.tile(
        pieces,
        KIND_LABEL[k],
        () => m().setKind(k),
        undefined,
        color,
        (t) => t.pieces('kind:' + k, () => kindPreview(k, material, color)),
      );
    });
    const shapes = this.page('shapes', 'Shapes');
    for (const s of SHAPES) this.tile(shapes, s.label, () => this.pickShape(s.id), shapeIcon(s.id));
    this.page('textures', 'Textures');
    const colours = this.page('colours', 'Colours');
    for (const c of PALETTE) this.tile(colours, c, () => this.pickColor(c), undefined, c);
    const custom = document.createElement('label');
    custom.className = 'tile';
    custom.innerHTML = '<span>Any colour</span>';
    const input = document.createElement('input');
    input.type = 'color';
    input.addEventListener('change', () => {
      this.pickColor(input.value);
      this.closeCatalog(true);
    });
    custom.prepend(input);
    colours.append(custom);
    const obstacles = this.page('obstacles', 'Obstacles');
    for (const p of PREFABS)
      this.tile(
        obstacles,
        p.label,
        () => m().setPrefab(p.id),
        undefined,
        '#ff8a2a',
        (t) =>
          t.pieces('prefab:' + p.id, () => [
            { min: [-176, -176, -48], max: [0, 176, 0], kind: 'start', material: 'tiles', color: '#4fd27a' },
            ...prefab(p.id, [0, 0, 0], 0, 128, ['planks', '#c76a2c'], ['stone', '#9a9a8a'], 3),
          ]),
      );
    this.page('scenery', 'Scenery');
    const skies = this.page('skies', 'Skies');
    THEMES.forEach((t, i) =>
      this.tile(
        skies,
        t.name,
        () => m().setTheme(i),
        undefined,
        `linear-gradient(${t.theme.skyTop}, ${t.theme.skyHorizon})`,
      ),
    );
    const motion = this.page('motion', 'Motion');
    for (const mp of MOVE_PRESETS)
      this.tile(motion, mp.label, () => (m().tool.move = mp.id), motionIcon(mp.id));
    const sliders = document.createElement('div');
    sliders.className = 'catalogSliders';
    sliders.innerHTML =
      '<label>Distance <span id="mvDistV"></span><input id="mvDist" type="range" min="32" max="1024" step="16"></label>' +
      '<label>Time (s) <span id="mvTimeV"></span><input id="mvTime" type="range" min="0.8" max="8" step="0.2"></label>';
    motion.append(sliders);
    sliders.querySelector<HTMLInputElement>('#mvDist')!.addEventListener('input', (e) => {
      m().tool.moveDist = Number((e.target as HTMLInputElement).value);
      $('mvDistV').textContent = String(m().tool.moveDist);
    });
    sliders.querySelector<HTMLInputElement>('#mvTime')!.addEventListener('input', (e) => {
      m().tool.movePeriod = Number((e.target as HTMLInputElement).value);
      $('mvTimeV').textContent = String(m().tool.movePeriod);
    });
  }

  /** Textures and scenery come from the asset manifest (and the drawn materials). */
  private fillCatalogAssets() {
    const m = () => this.model!;
    const tex = document.querySelector<HTMLElement>('.catalogPage[data-tab="textures"]')!;
    if (tex.childElementCount) return;
    for (const name of MATERIALS)
      this.tile(
        tex,
        name,
        () => this.pickMaterial(name),
        materialThumbnail(name, m()?.tool.color ?? '#c76a2c'),
      );
    const man = manifestNow();
    for (const t of man?.textures ?? [])
      this.tile(tex, t.label ?? t.group, () => this.pickMaterial(`img:${t.id}`), KENNEY_BASE + t.file);
    // Scenery by group (nature first, then each kit), each model drawn as it comes into view.
    const scen = document.querySelector<HTMLElement>('.catalogPage[data-tab="scenery"]')!;
    const groups = new Map<string, { id: string; group: string }[]>();
    for (const mo of man?.models ?? []) {
      const g = sceneryGroup(mo.group);
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(mo);
    }
    const order = ['Trees', 'Plants', 'Flowers', 'Rocks', 'Statues', 'Camp'];
    const rank = (g: string) => (order.includes(g) ? order.indexOf(g) : order.length);
    for (const [g, list] of [...groups].sort((a, b) => rank(a[0]) - rank(b[0]))) {
      const h = document.createElement('h4');
      h.className = 'catalogGroup';
      h.textContent = g;
      scen.append(h);
      for (const mo of list)
        this.tile(
          scen,
          modelLabel(mo.id),
          () => m().setProp(mo.id),
          undefined,
          groupColour(mo.group),
          (t) => t.model(mo.id),
        );
    }
    const d = document.getElementById('mvDist') as HTMLInputElement | null;
    if (d && this.model) {
      d.value = String(this.model.tool.moveDist);
      (document.getElementById('mvTime') as HTMLInputElement).value = String(this.model.tool.movePeriod);
    }
  }
}

function groupColour(g: string) {
  return g === 'tree'
    ? '#3f8f2e'
    : g === 'rock' || g === 'stone'
      ? '#8a8a8a'
      : g === 'flower'
        ? '#e04f9f'
        : g === 'plant' || g === 'grass'
          ? '#6fbf4a'
          : g === 'mushroom'
            ? '#d0503a'
            : '#a9744a';
}

/** Builder group of a manifest model group: Kenney's nature kit by kind, the kits by name. */
function sceneryGroup(g: string): string {
  const nature: Record<string, string> = {
    tree: 'Trees',
    rock: 'Rocks',
    stone: 'Rocks',
    plant: 'Plants',
    grass: 'Plants',
    flower: 'Flowers',
    mushroom: 'Plants',
    lily: 'Plants',
    cactus: 'Plants',
    crop: 'Plants',
    pot: 'Plants',
    statue: 'Statues',
    campfire: 'Camp',
  };
  return nature[g] ?? g;
}

/** A model id as a tile label: no kit prefix, words split. */
function modelLabel(id: string): string {
  return id
    .replace(/^(space|winter|spooky|toy|city|suburb|urban|camp|factory|crystal|castle|pirate|arena)-/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .toLowerCase();
}

/** How each piece kind looks in the catalog (and its picture). */
const KIND_LOOK: Record<string, [MaterialName, string]> = {
  block: ['planks', '#c76a2c'],
  solid: ['stone', '#9aa0a8'],
  checkpoint: ['tiles', '#5ad1ff'],
  start: ['tiles', '#4fd27a'],
  end: ['tiles', '#ff5a5a'],
  boost: ['neon', '#ffd23a'],
  deco: ['grass', '#5fae3e'],
  pad: ['neon', '#3affb0'],
  ladder: ['ladder', '#c89a5a'],
  water: ['water', '#2a8fc8'],
  wind: ['wind', '#9ad8ff'],
  gravity: ['wind', '#c39bff'],
};

/** A piece of each kind, sized like it usually is. */
function kindPreview(k: string, material: MaterialName, color: string): LevelBox[] {
  const box = (min: Vec3Tuple, max: Vec3Tuple, extra: Partial<LevelBox> = {}): LevelBox => ({
    min,
    max,
    kind: 'solid',
    material,
    color,
    ...extra,
  });
  switch (k) {
    case 'block':
      return [box([-64, -64, -24], [64, 64, 0])];
    case 'solid':
      return [box([-128, -64, -96], [128, 64, 0])];
    case 'boost':
      return [box([-96, -96, -32], [96, 96, 0])];
    case 'pad':
      return [box([-80, -80, -24], [80, 80, 0])];
    case 'ladder':
      return [box([-8, -48, -192], [8, 48, 192])];
    case 'water':
    case 'wind':
    case 'gravity':
      return [box([-128, -128, -128], [128, 128, 128])];
    case 'deco':
      return [
        box([-12, -12, -128], [12, 12, 0], { material: 'planks', color: '#7a5230', shape: 'cylinder' }),
        box([-64, -64, 0], [64, 64, 120], { shape: 'oct' }),
      ];
    default:
      return [box([-160, -160, -64], [160, 160, 0])];
  }
}

/** Small diagram of a motion preset: a piece and arrows for where it travels. */
function motionIcon(id: string): string {
  const arrows: Record<string, string> = {
    side: 'M30 50H8M14 44l-6 6 6 6M34 50h22M50 44l6 6-6 6',
    forward: 'M32 42l14 14M46 48v8h-8M32 42L18 28M18 36v-8h8',
    updown: 'M54 30V8M48 14l6-6 6 6M54 34v22M48 50l6 6 6-6',
    none: '',
  };
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="#4ae1ff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
    '<rect x="16" y="22" width="28" height="14" rx="2" fill="#ff8a2a" stroke="none"/>' +
    `<path d="${arrows[id] ?? ''}"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** Tiny outline icon of a shape, as an SVG data URL. */
function shapeIcon(id: string): string {
  const pts: Record<string, string> = {
    box: '8,8 56,8 56,56 8,56',
    cylinder: '',
    hex: '58,32 45,54 19,54 6,32 19,10 45,10',
    oct: '42,8 56,22 56,42 42,56 22,56 8,42 8,22 22,8',
    tri: '8,56 56,56 32,8',
    diamond: '32,6 58,32 32,58 6,32',
    ramp: '6,56 58,56 58,12',
  };
  const shape =
    id === 'cylinder' ? '<circle cx="32" cy="32" r="25"/>' : `<polygon points="${pts[id] ?? pts.box}"/>`;
  return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="#ff8a2a" stroke="#fff" stroke-width="3">${shape}</svg>`)}`;
}
